import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, CircularProgress, TextField, Grid, 
  Button, Pagination, Box, IconButton, Tooltip, FormControl,
  InputLabel, Select, MenuItem, Chip, OutlinedInput, TableSortLabel
} from '@mui/material';
import { Search, Edit, Delete, Visibility, Add, CheckCircle, Cancel } from '@mui/icons-material';
import axios from '../utils/api';
import { US_STATES } from '../utils/constants';

interface Lawyer {
  id: string;
  firstName: string;
  middleInitial?: string;
  lastName: string;
  suffix?: string;
  email: string;
  firmName: string;
  linkedInUrl?: string;
  emailVerified: boolean;
  phoneNumbers: Array<{ number: string; type: string }>;
  primaryOffice: {
    city: string;
    state: string;
  };
  practiceAreas: Array<{
    practiceArea?: { name: string }; // Legacy format
    category?: { id: string; name: string };
    subcategory?: { id: string; name: string };
    otherArea?: string;
    subCategoryCustomText?: string;
    percentage?: number | null;
  }>;
  stateLicenses: Array<{ state: string; isActive: boolean; inGoodStanding: boolean }>;
  proBonoInterest: boolean;
  feeInfo?: {
    hourlyRate?: number;
    otherFees?: string;
  };
  createdAt: string;
  registrationType?: 'directory' | 'referral';
}

const LawyerList: React.FC = () => {
  const location = useLocation();
  const isManagementMode = location.pathname.startsWith('/management');
  const isDirectoryMode = location.pathname.startsWith('/directory');
  
  const [lawyers, setLawyers] = useState<Lawyer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalUnfiltered, setTotalUnfiltered] = useState(0);
  const [error, setError] = useState<string | null>(null);
  
  // Enhanced search filters
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [practiceAreaFilter, setPracticeAreaFilter] = useState<string[]>([]);
  const [proBonoFilter, setProBonoFilter] = useState('');
  const [freeConsultationFilter, setFreeConsultationFilter] = useState('');
  const [liabilityInsuranceFilter, setLiabilityInsuranceFilter] = useState('');
  const [registrationTypeFilter, setRegistrationTypeFilter] = useState('');
  const [minFee, setMinFee] = useState('');
  const [maxFee, setMaxFee] = useState('');
  const [searchTrigger, setSearchTrigger] = useState(0); // Used to trigger search manually
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [practiceAreaCategories, setPracticeAreaCategories] = useState<Array<{
    id: string;
    name: string;
  }>>([]);

  const itemsPerPage = 10;

  // Fetch practice area categories for filter dropdown
  useEffect(() => {
    const fetchPracticeAreas = async () => {
      try {
        const response = await axios.get('/api/practice-areas');
        setPracticeAreaCategories(response.data.map((cat: any) => ({ id: cat.id, name: cat.name })));
      } catch (error) {
        console.error('Error fetching practice areas:', error);
      }
    };
    fetchPracticeAreas();
  }, []);

  const fetchLawyers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
        search: searchTerm,
        searchBy: 'name', // Always search by name (which includes lawyer name and firm name)
        states: stateFilter.join(','),
        practiceAreas: practiceAreaFilter.join(','),
        proBono: proBonoFilter,
        freeConsultation: freeConsultationFilter,
        liabilityInsurance: liabilityInsuranceFilter,
        registrationType: registrationTypeFilter,
        minFee: minFee,
        maxFee: maxFee,
        sortBy,
        sortDir
      });
      
      // Include unverified lawyers in management mode
      if (isManagementMode) {
        params.append('includeUnverified', 'true');
      }
      
      const response = await axios.get(`/api/lawyers?${params}`);
      const lawyersData = response.data.lawyers || response.data;
      const total = response.data.total ?? lawyersData.length;
      setLawyers(lawyersData);
      setTotalCount(total);
      setTotalUnfiltered(response.data.totalUnfiltered ?? total);
      setTotalPages(Math.ceil(total / itemsPerPage));
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to fetch lawyers');
      setLawyers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and on any search trigger or page change (including back to page 1)
  useEffect(() => {
    fetchLawyers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTrigger, page]);

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const clearAllFilters = () => {
    setStateFilter([]);
    setPracticeAreaFilter([]);
    setProBonoFilter('');
    setFreeConsultationFilter('');
    setLiabilityInsuranceFilter('');
    setRegistrationTypeFilter('');
    setMinFee('');
    setMaxFee('');
  };

  const triggerSearch = () => {
    setSearchTrigger(prev => prev + 1);
    setPage(1);
  };

  const handleSort = (column: string) => {
    const isSameColumn = sortBy === column;
    const nextDir: 'asc' | 'desc' = isSameColumn && sortDir === 'asc' ? 'desc' : 'asc';
    setSortBy(column);
    setSortDir(nextDir);
    setPage(1);
    setSearchTrigger(prev => prev + 1);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (stateFilter.length > 0) count++;
    if (practiceAreaFilter.length > 0) count++;
    if (proBonoFilter !== '') count++;
    if (freeConsultationFilter !== '') count++;
    if (liabilityInsuranceFilter !== '') count++;
    if (registrationTypeFilter !== '') count++;
    if (minFee) count++;
    if (maxFee) count++;
    return count;
  };

  const handleDelete = async (lawyerId: string) => {
    if (window.confirm('Are you sure you want to delete this lawyer?')) {
      try {
        await axios.delete(`/api/lawyers/${lawyerId}`);
        fetchLawyers();
      } catch (err: any) {
        alert('Failed to delete lawyer: ' + (err?.response?.data?.error || 'Unknown error'));
      }
    }
  };

  const getPracticeAreas = (lawyer: Lawyer) => {
    const areas = lawyer.practiceAreas
      .map(pa => {
        // New format: category:subcategory
        if (pa.category && pa.subcategory) {
          let displayName = `${pa.category.name}: ${pa.subcategory.name}`;
          if (pa.subCategoryCustomText) {
            displayName += ` - ${pa.subCategoryCustomText}`;
          }
          return displayName;
        }
        // "Other" category: category but no subcategory, has otherArea
        if (pa.category && !pa.subcategory && pa.otherArea) {
          return `${pa.category.name}: ${pa.otherArea}`;
        }
        // Legacy format: practiceArea name
        if (pa.practiceArea?.name) {
          return pa.practiceArea.name;
        }
        // Other area (legacy)
        if (pa.otherArea) {
          return pa.otherArea;
        }
        return null;
      })
      .filter(Boolean)
      .join(', ');
    
    if (areas.length > 30) {
      return areas.substring(0, 30) + '...';
    }
    return areas;
  };

  const getActiveStates = (lawyer: Lawyer) => {
    return lawyer.stateLicenses
      .filter(license => license.isActive && license.inGoodStanding)
      .map(license => license.state)
      .join(', ');
  };

  const getFeeDisplay = (lawyer: Lawyer) => {
    if (!lawyer.feeInfo?.hourlyRate) return 'Not specified';
    return `$${lawyer.feeInfo.hourlyRate}/hr`;
  };

  const getTitle = () => {
    if (isManagementMode) return 'Lawyer Management';
    if (isDirectoryMode) return 'Lawyer Directory';
    return 'Lawyer Directory';
  };

  const getAddButtonText = () => {
    if (isManagementMode) return 'Add Lawyer';
    return 'Register New Lawyer';
  };

  const getAddButtonPath = () => {
    if (isManagementMode) return '/management/add';
    return '/registration';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {getTitle()}
      </Typography>

      {/* Basic Search Section */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Search lawyers"
            value={searchTerm}
            onChange={handleSearchInputChange}
            placeholder="Search by lawyer name or firm name..."
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button 
            variant="contained" 
            onClick={triggerSearch}
            startIcon={<Search />}
            fullWidth
          >
            Search
          </Button>
        </Grid>
        {isManagementMode && (
          <Grid item xs={12} sm={4}>
            <Button 
              variant="contained" 
              onClick={() => window.location.href = getAddButtonPath()}
              startIcon={<Add />}
              fullWidth
            >
              {getAddButtonText()}
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Advanced Filters Section */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Search Filters
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Active State(s)</InputLabel>
                <Select
                  multiple
                  value={stateFilter}
                  onChange={(e) => {
                    setStateFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
                  }}
                  input={<OutlinedInput label="State(s)" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                    {US_STATES.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Practice Area(s)</InputLabel>
                <Select
                  multiple
                  value={practiceAreaFilter}
                  onChange={(e) => {
                    setPracticeAreaFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
                  }}
                  input={<OutlinedInput label="Practice Area(s)" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {practiceAreaCategories.map((category) => (
                    <MenuItem key={category.id} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Pro Bono Interest</InputLabel>
                <Select
                  value={proBonoFilter}
                  label="Pro Bono Interest"
                  onChange={(e) => {
                    setProBonoFilter(e.target.value);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Free Consultation</InputLabel>
                <Select
                  value={freeConsultationFilter}
                  label="Free Consultation"
                  onChange={(e) => {
                    setFreeConsultationFilter(e.target.value);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Liability Insurance</InputLabel>
                <Select
                  value={liabilityInsuranceFilter}
                  label="Liability Insurance"
                  onChange={(e) => {
                    setLiabilityInsuranceFilter(e.target.value);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Registration Type</InputLabel>
                <Select
                  value={registrationTypeFilter}
                  label="Registration Type"
                  onChange={(e) => {
                    setRegistrationTypeFilter(e.target.value);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="directory">Directory</MenuItem>
                  <MenuItem value="referral">Referral Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  label="Min Fee ($/hr)"
                  type="number"
                  value={minFee}
                  onChange={(e) => {
                    setMinFee(e.target.value);
                  }}
                  placeholder="0"
                  size="small"
                />
                <Typography>-</Typography>
                <TextField
                  label="Max Fee ($/hr)"
                  type="number"
                  value={maxFee}
                  onChange={(e) => {
                    setMaxFee(e.target.value);
                  }}
                  placeholder="1000"
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={1}>
                <Button 
                  variant="outlined" 
                  onClick={clearAllFilters}
                  disabled={getActiveFiltersCount() === 0}
                >
                  Clear All Filters
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  {getActiveFiltersCount() > 0 && `${getActiveFiltersCount()} filter(s) active`}
                </Typography>
              </Box>
            </Grid>
          </Grid>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Showing {totalCount} of {totalUnfiltered} registered lawyers
        </Typography>
      )}

      {/* Lawyers Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'name' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'name'} direction={sortBy === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'type' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'type'} direction={sortBy === 'type' ? sortDir : 'asc'} onClick={() => handleSort('type')}>
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'firm' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'firm'} direction={sortBy === 'firm' ? sortDir : 'asc'} onClick={() => handleSort('firm')}>
                  Firm
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'location' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'location'} direction={sortBy === 'location' ? sortDir : 'asc'} onClick={() => handleSort('location')}>
                  Location
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ maxWidth: 150 }}>Practice Areas</TableCell>
              <TableCell>Active States</TableCell>
              <TableCell sortDirection={sortBy === 'proBono' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'proBono'} direction={sortBy === 'proBono' ? sortDir : 'asc'} onClick={() => handleSort('proBono')}>
                  Pro Bono
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'hourlyRate' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'hourlyRate'} direction={sortBy === 'hourlyRate' ? sortDir : 'asc'} onClick={() => handleSort('hourlyRate')}>
                  Hourly Rate
                </TableSortLabel>
              </TableCell>
              {isManagementMode && (
                <TableCell sortDirection={sortBy === 'verified' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'verified'} direction={sortBy === 'verified' ? sortDir : 'asc'} onClick={() => handleSort('verified')}>
                    Verified
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell sortDirection={sortBy === 'created' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'created'} direction={sortBy === 'created' ? sortDir : 'asc'} onClick={() => handleSort('created')}>
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lawyers.map(lawyer => (
              <TableRow key={lawyer.id}>
                <TableCell>
                  {lawyer.firstName} {lawyer.middleInitial} {lawyer.lastName} {lawyer.suffix}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={lawyer.registrationType === 'referral' ? 'Referral' : 'Directory'}
                    color={lawyer.registrationType === 'referral' ? 'default' : 'primary'}
                    variant={lawyer.registrationType === 'referral' ? 'outlined' : 'filled'}
                  />
                </TableCell>
                <TableCell>{lawyer.firmName}</TableCell>
                <TableCell>
                  {lawyer.primaryOffice?.city}, {lawyer.primaryOffice?.state}
                </TableCell>
                <TableCell sx={{ maxWidth: 150 }}>
                  <Typography variant="body2" noWrap>
                    {getPracticeAreas(lawyer)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {getActiveStates(lawyer)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={lawyer.proBonoInterest ? 'Yes' : 'No'} 
                    color={lawyer.proBonoInterest ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getFeeDisplay(lawyer)}
                  </Typography>
                </TableCell>
                {isManagementMode && (
                  <TableCell>
                    <Chip
                      icon={lawyer.emailVerified ? <CheckCircle /> : <Cancel />}
                      label={lawyer.emailVerified ? 'Verified' : 'Unverified'}
                      color={lawyer.emailVerified ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                )}
                <TableCell>
                  {lawyer.createdAt ? new Date(lawyer.createdAt).toLocaleDateString() : ''}
                </TableCell>
                {isManagementMode ? (
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => window.location.href = `/management/lawyer/${lawyer.id}`}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => window.location.href = `/management/edit/${lawyer.id}`}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(lawyer.id)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                ) : (
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => window.location.href = `/directory/lawyer/${lawyer.id}`}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {lawyers.length === 0 && !loading && (
        <Typography variant="body1" textAlign="center" sx={{ mt: 3 }}>
          No lawyers found. Try adjusting your search criteria.
        </Typography>
      )}
    </Paper>
  );
};

export default LawyerList; 